import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, Users, Heart, Zap, Shield, Smile, ArrowRight, Star } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
const Welcome = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const handleGetStarted = () => {
    const inviteCode = searchParams.get('invite');
    if (inviteCode) {
      navigate(`/auth?invite=${inviteCode}`);
    } else {
      navigate('/auth?register=true');
    }
  };
  return <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 dark:from-gray-900 dark:via-purple-900 dark:to-blue-900">
      {/* Header */}
      <header className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/lovable-uploads/e9ee540f-ae0a-4d24-b1a2-69eb3153196d.png" alt="Positivio" className="h-10" />
            <span className="text-lg font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
              Positivio
            </span>
          </div>
          <Button variant="outline" onClick={() => navigate('/auth')} className="hover:bg-purple-50">
            Sign In
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 text-center py-0">
        <div className="max-w-4xl mx-auto">
          {/* Big Logo */}
          <div className="flex justify-center mb-8">
            <img src="/lovable-uploads/e9ee540f-ae0a-4d24-b1a2-69eb3153196d.png" alt="Positivio" className="h-32 md:h-40" />
          </div>
          
          <Badge className="mb-6 bg-gradient-to-r from-pink-100 to-purple-100 text-purple-800 border-purple-200">
            The Social Media Alternative You've Been Looking For
          </Badge>
          
          <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-pink-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">
            See the Good in People
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
            An exclusive invite-only community where only real, engaged, positive users connect.
            No bots, no fake accounts - just authentic humans who value genuine connections.
          </p>
          
          <div className="bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 border border-purple-300 dark:border-purple-700 rounded-lg p-6 mb-6 max-w-2xl mx-auto">
            <p className="text-purple-800 dark:text-purple-200 font-bold text-lg mb-2">
              🛡️ Exclusive Community • Real Users Only
            </p>
            <p className="text-purple-700 dark:text-purple-300 text-sm mb-2">
              • Only authentic, engaged, positive users<br/>
              • No bots or fake accounts<br/>
              • Premium quality connections for $2.99/month
            </p>
            <p className="text-purple-600 dark:text-purple-400 text-xs font-medium">
              Invite-only access ensures brand prestige and viral growth through exclusivity
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <Button size="lg" onClick={handleGetStarted} className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white px-8 py-3 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300">
              Sign Up with Invite Code
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              onClick={() => navigate('/auth')} 
              className="border-purple-300 text-purple-600 hover:bg-purple-50 px-8 py-3 text-lg rounded-full"
            >
              Request an Invite Code
            </Button>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Have an invite code? Sign up above • Premium at $2.99/month
          </p>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
            What Makes Positivio Different?
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            Everything traditional social media isn't
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Traditional Social Media */}
          <Card className="border-red-200 bg-red-50/50 dark:bg-red-900/20">
            <CardHeader>
              <CardTitle className="text-red-700 dark:text-red-400 text-center">
                Traditional Social Media
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3 text-red-600 dark:text-red-400">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span>Constant ads and sponsored content</span>
              </div>
              <div className="flex items-center gap-3 text-red-600 dark:text-red-400">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span>Algorithms deciding what you see</span>
              </div>
              <div className="flex items-center gap-3 text-red-600 dark:text-red-400">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span>Data tracking and privacy invasion</span>
              </div>
              <div className="flex items-center gap-3 text-red-600 dark:text-red-400">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span>Fake followers and artificial engagement</span>
              </div>
              <div className="flex items-center gap-3 text-red-600 dark:text-red-400">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span>Designed to maximize screen time</span>
              </div>
            </CardContent>
          </Card>

          {/* Positivio */}
          <Card className="border-green-200 bg-green-50/50 dark:bg-green-900/20">
            <CardHeader>
              <CardTitle className="text-green-700 dark:text-green-400 text-center">
                Positivio Experience
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3 text-green-600 dark:text-green-400">
                <Check className="w-5 h-5" />
                <span>100% ad-free environment</span>
              </div>
              <div className="flex items-center gap-3 text-green-600 dark:text-green-400">
                <Check className="w-5 h-5" />
                <span>Chronological feed - you control what you see</span>
              </div>
              <div className="flex items-center gap-3 text-green-600 dark:text-green-400">
                <Check className="w-5 h-5" />
                <span>Zero tracking, complete privacy</span>
              </div>
              <div className="flex items-center gap-3 text-green-600 dark:text-green-400">
                <Check className="w-5 h-5" />
                <span>Real humans only - verified by payment</span>
              </div>
              <div className="flex items-center gap-3 text-green-600 dark:text-green-400">
                <Check className="w-5 h-5" />
                <span>Designed for wellbeing and authenticity</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Features */}
      <section className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
            Built for Human Connection
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            Features that prioritize your wellbeing over engagement metrics
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <Card className="hover:shadow-lg transition-all duration-300">
            <CardHeader className="text-center">
              <Heart className="w-12 h-12 mx-auto mb-4 text-pink-500" />
              <CardTitle>Authentic Connections</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 dark:text-gray-300 text-center">
                Connect with real friends, not influencers. Build meaningful relationships in a space free from social pressure.
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-all duration-300">
            <CardHeader className="text-center">
              <Shield className="w-12 h-12 mx-auto mb-4 text-blue-500" />
              <CardTitle>Privacy First</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 dark:text-gray-300 text-center">
                Your data stays yours. No tracking, no profiling, no selling your information to advertisers.
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-all duration-300">
            <CardHeader className="text-center">
              <Smile className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
              <CardTitle>Mental Wellbeing</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 dark:text-gray-300 text-center">
                Designed to make you feel good. No comparison culture, no endless scrolling, just positive interactions.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Benefits */}
      <section className="container mx-auto px-4 py-16 bg-gradient-to-r from-purple-100/50 to-pink-100/50 dark:from-purple-900/30 dark:to-pink-900/30 rounded-3xl mx-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
            The Psychology Behind Better Social Media
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            How Positivio supports your mental health and wellbeing
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <Zap className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="font-semibold mb-2 text-gray-900 dark:text-white">Reduced Anxiety</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              No algorithm pressure means less social comparison and FOMO
            </p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="font-semibold mb-2 text-gray-900 dark:text-white">Better Relationships</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Focus on quality connections with people who actually matter to you
            </p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <Star className="w-8 h-8 text-purple-600 dark:text-purple-400" />
            </div>
            <h3 className="font-semibold mb-2 text-gray-900 dark:text-white">Authentic Self</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Be yourself without worrying about likes, followers, or viral content
            </p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-pink-100 dark:bg-pink-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart className="w-8 h-8 text-pink-600 dark:text-pink-400" />
            </div>
            <h3 className="font-semibold mb-2 text-gray-900 dark:text-white">Mindful Usage</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Designed for intentional use, not addictive endless scrolling
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-4 py-16 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900 dark:text-white">
            Ready for a Healthier Social Experience?
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
            Join our exclusive community of verified, engaged, positive users who value authentic connections. 
            Quality over quantity - real humans only, for just $2.99/month.
          </p>
          <Button size="lg" onClick={handleGetStarted} className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white px-8 py-3 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300">
            Register with Invite Code
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="container mx-auto px-4 py-8 border-t border-gray-200 dark:border-gray-700">
        <div className="text-center text-gray-600 dark:text-gray-400">
          <p>© 2024 Positivio. Building a kinder internet, one connection at a time.</p>
        </div>
      </footer>
    </div>;
};
export default Welcome;